import { useCallback, useContext, useEffect, useRef } from "react";
import { _logStartExperiment, _logConvertExperiment, logEvent } from "@src/utils/logClient";
import { isProduction } from "@src/utils/env";
import { useFeatureFlag, useFeatureFlagsWithOverrides } from "./useFeatureFlag";
import { featureSwitches, type FeatureSwitch } from "./experiments/constants";
import { AppDataContext } from "@src/components/AppDataContext";
import { MobileDataContext } from "@src/components/mobile/MobileDataContext";
import { HoldoutData } from "@src/types/featureSwitches";
import { getFeatureSwitchOverride } from "@src/components/devTools/devToolsSettings";

// Helper function to check if a variant is a holdout variant
export const isHoldoutVariant = (variant: string) => {
  return ["baseline", "shipped_improvements"].includes(variant);
};

// Hook to get holdout data from the appropriate context
function useHoldoutData(): HoldoutData | undefined {
  const isMobile = process.env.GATSBY_BUILD_WEBSITE === "mobile";
  // @ts-expect-error property set is missing
  const ctx: React.Context<{ data: { holdoutData?: HoldoutData } }> = isMobile ? MobileDataContext : AppDataContext;
  const appData = useContext(ctx);
  return appData.data.holdoutData;
}

// to be called on mount in a component
export function useStartExperiment(experiment: string, overrideFS?: Record<string, string>): void {
  // const appData = useContext(AppDataContext);
  // const featureFlags = appData?.data?.featureFlags;
  const featureFlags: Record<string, string> | undefined = useFeatureFlagsWithOverrides();
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (hasStartedRef.current) return;

    if (!featureFlags || !Object.keys(featureFlags).length) {
      if (!isProduction()) {
        console.log(`failed trying to start experiment ${experiment} - feature flags not available yet`);
      }
      return;
    }

    if (!featureFlags[experiment]) {
      if (!isProduction()) {
        console.log(`no feature flag data to start experiment ${experiment}`);
      }
      return;
    }

    if (featureFlags[experiment] === "off") {
      !isProduction() && console.log(`cannot start experiment for feature switch ${experiment} with value of off`);
      return;
    }

    !isProduction() && console.log(`startExperiment for ${experiment}`);
    _logStartExperiment(experiment, { ...featureFlags, ...(overrideFS || {}) });
    hasStartedRef.current = true;
  }, [experiment, featureFlags, overrideFS]);
}

// defined along with any other hooks at the start of a component
// syntax is: top of file: const experimentNameConvert = useCreateConvertExperimentEvent(experimentName)
// in the onClick, or wherever you convert: experimentNameConvert()

export function useCreateConvertExperimentEvent(experiment: string): () => void {
  const featureFlags: Record<string, string> | undefined = useFeatureFlagsWithOverrides();

  if (!featureFlags || !Object.keys(featureFlags).length) {
    return () => {
      if (!isProduction()) {
        throw new Error(`failed trying to convert experiment ${experiment} - feature flags not available yet`);
      }
    };
  }

  if (!featureFlags[experiment]) {
    return () => {
      if (!isProduction()) {
        throw new Error(`no feature flag data for converting experiment ${experiment}`);
      }
    };
  }

  if (featureFlags[experiment] === "off") {
    return () => {
      !isProduction() && console.log(`cannot convert experiment for feature switch ${experiment} with value of off`);
    };
  }

  return () => {
    !isProduction() && console.log(`convertExperiment for ${experiment}`);
    _logConvertExperiment(experiment);
  };
}

export function useStartExperimentWhenInAudience(
  experiment: (typeof featureSwitches)[number],
  { isInAudience }: { isInAudience: boolean },
): string | undefined {
  const data = useFeatureFlagsWithOverrides();
  const holdoutDataRaw = useHoldoutData();

  if (!data) return "off";
  const holdoutData = holdoutDataRaw ?? {};
  return _startExperiment({
    experiment,
    data: data as Record<typeof experiment, string>,
    holdoutData,
    isInAudience,
  });
}

export function useExperimentVariantWithExperimentStart({
  experiment,
  isInAudience,
  expectedVariant,
}: {
  experiment: (typeof featureSwitches)[number];
  isInAudience: boolean;
  expectedVariant: string;
}) {
  useStartExperimentWhenInAudience(experiment, {
    isInAudience,
  });
  const variant = useFeatureFlag(experiment);

  if (!isInAudience) {
    return false;
  }

  return variant.actualValue === expectedVariant;
}

function _startExperiment<T extends string>({
  experiment,
  data,
  holdoutData,
  isInAudience,
}: {
  experiment: T;
  data: Record<T, string>;
  holdoutData: HoldoutData;
  isInAudience: boolean;
}): string {
  const handleError = (msg: string) => {
    if (!isProduction()) {
      throw new Error(msg);
    } else {
      // eslint-disable-next-line no-console
      console.error(msg);
    }
    return "off";
  };
  if (!experiment) return handleError(`Invalid experiment name: ${experiment}`);
  if (Object.entries(data).length === 0) return "off";

  if (!isInAudience) return "off";
  const holdoutDataForExperiment = holdoutData?.[experiment];
  const experimentVariant = data[experiment];
  // variantOverride takes precedence over dev tools override
  const manualOverride = getFeatureSwitchOverride(experiment);

  // if no holdout data for experiment, log start experiment and return experiment variant
  if (!holdoutDataForExperiment) {
    if (!experimentVariant) {
      return handleError(`Invalid experiment name: ${experiment}`);
    }
    if (experimentVariant === "off") return "off";
    _logStartExperiment(experiment, data);
    !isProduction() && console.log(`startExperiment for ${experiment}`);
    return manualOverride || experimentVariant;
  }

  // if holdout data for experiment is shipped, log start holdout and return shipped variant
  if (holdoutDataForExperiment.status === "shipped") {
    const { shippedVariant, holdoutName } = holdoutDataForExperiment;
    const holdoutVariant = data[holdoutName as T];
    if (isHoldoutVariant(holdoutVariant)) {
      logEvent({
        eventName: `holdout.started_for_experiment`,
        eventValue: experiment,
        metadata: {
          holdout: holdoutName,
          experiment,
          shippedVariant,
          holdoutVariant,
        },
      });
      _logStartExperiment(holdoutName, data);
      !isProduction() && console.log(`startExperiment for ${holdoutName}`);
      // if user should not see the shipped variant (they're in holdout), return off
      if (holdoutVariant === "baseline") {
        return "off";
      }
    }

    // if user should see the shipped variant (shipped_improvements, off), return the shipped variant
    // this is equivalent to returning `experimentVariant` since it starts with the holdout value and is
    // overwritten by the DevTools override, but we use manualOverride do be explicit about the order of precedence.
    // and making sure that in production we always default to the shipped variant.
    return manualOverride || shippedVariant;
  }

  const { holdoutName } = holdoutDataForExperiment;
  const holdoutVariant = data[holdoutName as T];

  // exclude users in holdout from experiment
  if (isHoldoutVariant(holdoutVariant)) {
    return "off";
  }
  if (experimentVariant === "off") return "off";
  _logStartExperiment(experiment, data);
  !isProduction() && console.log(`startExperiment for ${experiment}`);
  return manualOverride || experimentVariant;
}

export function useLazyStartExperiment(
  experiment: FeatureSwitch,
): ({ isInAudience }: { isInAudience: boolean }) => string {
  const data = useFeatureFlagsWithOverrides();
  const holdoutDataRaw = useHoldoutData();

  const startExperiment = useCallback(
    ({ isInAudience }: { isInAudience: boolean }) => {
      if (!data) return "off";
      const holdoutData = holdoutDataRaw ?? {};
      return _startExperiment({
        experiment,
        data,
        holdoutData,
        isInAudience: isInAudience,
      });
    },
    [data, experiment, holdoutDataRaw],
  );

  return startExperiment;
}

export function useStartExperimentWhenInAudienceWithVariant(
  experiment: FeatureSwitch,
  {
    isInAudience,
  }: {
    isInAudience: boolean;
  },
): string {
  const data = useFeatureFlagsWithOverrides();
  const holdoutDataRaw = useHoldoutData();

  if (!data) return "off";
  const holdoutData = holdoutDataRaw ?? {};
  return _startExperiment({
    experiment,
    data: data,
    holdoutData,
    isInAudience,
  });
}
